import React, { useState, useMemo } from 'react';
import { FiCheck, FiX, FiAlertCircle, FiClock, FiUser, FiMapPin, FiCheckCircle, FiAlertTriangle, FiGift } from 'react-icons/fi';
import { useSelector } from 'react-redux';

// Get PST date string
function getPSTDateString(date = new Date()) {
  const pstDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const year = pstDate.getFullYear();
  const month = String(pstDate.getMonth() + 1).padStart(2, '0');
  const day = String(pstDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getOrderDateInPST(inStockDate) {
  if (!inStockDate) return null;
  return getPSTDateString(new Date(inStockDate));
}

export default function MorningCheckMode({ 
  orders, 
  completedOrders,
  onVerifyOrder,
  onReportError,
  onClose 
}) {
  const { adminInfo } = useSelector(s => s.login);
  const checkerName = adminInfo ? `${adminInfo.firstName || ''} ${adminInfo.lastName || ''}`.trim() : 'Unknown';
  
  // Get today's date
  const todayStr = getPSTDateString();
  
  // Filter orders for today
  const todayOrders = useMemo(() => {
    const allOrders = [...orders, ...completedOrders];
    return allOrders.filter(o => getOrderDateInPST(o.inStockDate) === todayStr);
  }, [orders, completedOrders, todayStr]);

  // Track local verification state for each order
  const [verificationState, setVerificationState] = useState({});
  const [errorModalOrder, setErrorModalOrder] = useState(null);
  const [errorNote, setErrorNote] = useState('');
  const [errorField, setErrorField] = useState('');
  
  // Get verification state for an order
  const getOrderVerification = (orderId) => {
    return verificationState[orderId] || {
      pickupTimeVerified: false,
      entryVerified: false,
      deliveryTimeVerified: false,
      driverVerified: false,
    };
  };

  // Toggle a verification checkbox
  const toggleVerification = (orderId, field) => {
    setVerificationState(prev => ({
      ...prev,
      [orderId]: {
        ...getOrderVerification(orderId),
        [field]: !getOrderVerification(orderId)[field]
      }
    }));
  };

  // Check if all verifications are complete for an order
  const isOrderFullyVerified = (order) => {
    const v = getOrderVerification(order._id);
    return v.pickupTimeVerified && v.entryVerified && v.deliveryTimeVerified && v.driverVerified;
  };

  // Count verified orders
  const verifiedCount = todayOrders.filter(o => o.morningCheck?.checkedAt || isOrderFullyVerified(o)).length;
  const totalCount = todayOrders.length;

  // Handle verify order
  const handleVerify = async (order) => {
    if (!isOrderFullyVerified(order)) return;
    
    await onVerifyOrder(order._id, {
      checkedBy: adminInfo?._id,
      checkedByName: checkerName,
      checkedAt: new Date().toISOString(),
      errorsFound: [],
    });
  };

  // Handle report error
  const handleReportError = async () => {
    if (!errorModalOrder || !errorField || !errorNote.trim()) return;
    
    await onReportError(errorModalOrder._id, {
      field: errorField,
      note: errorNote,
      reportedBy: adminInfo?._id,
      reportedByName: checkerName,
      reportedAt: new Date().toISOString(),
    });
    
    setErrorModalOrder(null);
    setErrorNote('');
    setErrorField('');
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80 z-50 overflow-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-white">☀️ Morning Check</h1>
                  <p className="text-amber-100 text-sm">Today's Orders - {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
                >
                  Exit Check
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <FiUser className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Checking as:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{checkerName}</span>
                  </div>
                </div>
                
                {/* Progress */}
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Progress: <span className="font-bold text-amber-600 dark:text-amber-400">{verifiedCount}</span> of {totalCount} verified
                  </div>
                  <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 transition-all duration-300"
                      style={{ width: `${totalCount > 0 ? (verifiedCount / totalCount) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Incentive Banner */}
            <div className="px-6 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-3">
                <FiGift className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  Find an error? Report it for a bonus! Help us maintain quality.
                </span>
              </div>
            </div>
          </div>

          {/* Orders List */}
          {todayOrders.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center">
              <FiCheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Orders for Today!</h2>
              <p className="text-gray-600 dark:text-gray-400">There are no orders scheduled for delivery today.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {todayOrders.map((order) => {
                const v = getOrderVerification(order._id);
                const isVerified = isOrderFullyVerified(order);
                const isChecked = !!order.morningCheck?.checkedAt;
                const hasErrors = order.morningCheck?.errorsFound?.length > 0;
                
                // EOD Review info
                const eodReviewer = order.eodReview?.reviewedByName || 'Unknown';
                const eodReviewedAt = order.eodReview?.reviewedAt 
                  ? new Date(order.eodReview.reviewedAt).toLocaleString()
                  : 'Not reviewed';

                return (
                  <div 
                    key={order._id}
                    className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border-2 ${
                      isChecked ? 'border-emerald-500' : 
                      hasErrors ? 'border-red-500' :
                      'border-transparent'
                    }`}
                  >
                    {/* Order Header */}
                    <div className={`px-6 py-4 ${isChecked ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="font-mono font-bold text-lg text-gray-900 dark:text-white">
                            #{order.uniqueId}
                          </span>
                          {order.storeArrivalTime && (
                            <span className="px-3 py-1 bg-gray-200 dark:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200">
                              {new Date(order.storeArrivalTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}
                            </span>
                          )}
                        </div>
                        
                        {isChecked && (
                          <span className="flex items-center gap-2 px-3 py-1 bg-emerald-500 text-white rounded-full text-sm font-medium">
                            <FiCheck className="w-4 h-4" /> Verified
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <FiMapPin className="w-4 h-4" />
                        <span>{order.property?.address?.line1}, {order.property?.address?.city}</span>
                      </div>
                      
                      {/* EOD Review Info */}
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        EOD Review by <span className="font-medium">{eodReviewer}</span> • {eodReviewedAt}
                      </div>
                    </div>

                    {/* Verification Checklist */}
                    <div className="px-6 py-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                        Cross-Verification Checklist
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Pickup Time */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.pickupTimeVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.pickupTimeVerified}
                            onChange={() => toggleVerification(order._id, 'pickupTimeVerified')}
                            disabled={isChecked}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">Pickup time still matches grocer</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Re-verify on grocer website</p>
                          </div>
                        </label>

                        {/* Entry Instructions */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.entryVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.entryVerified}
                            onChange={() => toggleVerification(order._id, 'entryVerified')}
                            disabled={isChecked}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">Entry instructions look correct</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {order.entryInstructions ? `"${order.entryInstructions.slice(0, 40)}..."` : 'No instructions'}
                            </p>
                          </div>
                        </label>

                        {/* Delivery Time */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.deliveryTimeVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.deliveryTimeVerified}
                            onChange={() => toggleVerification(order._id, 'deliveryTimeVerified')}
                            disabled={isChecked}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">Delivery time makes sense</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {order.storeArrivalTime ? new Date(order.storeArrivalTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Not set'}
                            </p>
                          </div>
                        </label>

                        {/* Driver */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.driverVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.driverVerified}
                            onChange={() => toggleVerification(order._id, 'driverVerified')}
                            disabled={isChecked}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">Driver confirmed</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {order.driverID ? 'Driver assigned' : 'No driver'}
                            </p>
                          </div>
                        </label>
                      </div>

                      {/* Reported Errors */}
                      {hasErrors && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <h4 className="font-medium text-red-700 dark:text-red-300 text-sm mb-2 flex items-center gap-2">
                            <FiAlertTriangle className="w-4 h-4" />
                            Errors Reported
                          </h4>
                          <ul className="space-y-1">
                            {order.morningCheck.errorsFound.map((error, idx) => (
                              <li key={idx} className="text-sm text-red-600 dark:text-red-400">
                                • <span className="font-medium">{error.field}:</span> {error.note}
                                <span className="text-xs text-red-500 ml-2">
                                  ({error.reportedByName} at {new Date(error.reportedAt).toLocaleTimeString()})
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Action Footer */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setErrorModalOrder(order)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg font-medium text-sm transition-colors"
                        >
                          <FiAlertCircle className="w-4 h-4" />
                          Report Error
                        </button>

                        <div className="flex items-center gap-4">
                          {isChecked && order.morningCheck && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Verified by {order.morningCheck.checkedByName} at {new Date(order.morningCheck.checkedAt).toLocaleTimeString()}
                            </div>
                          )}

                          <button
                            onClick={() => handleVerify(order)}
                            disabled={!isVerified || isChecked}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-colors ${
                              isChecked ? 'bg-emerald-100 text-emerald-700 cursor-default' :
                              isVerified ? 'bg-amber-500 hover:bg-amber-600 text-white' : 
                              'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isChecked ? (
                              <>
                                <FiCheck className="w-5 h-5" />
                                Verified
                              </>
                            ) : (
                              <>
                                <FiCheckCircle className="w-5 h-5" />
                                Verify Order
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Completion Banner */}
          {verifiedCount === totalCount && totalCount > 0 && (
            <div className="mt-6 bg-emerald-500 rounded-2xl p-6 text-center text-white">
              <FiCheckCircle className="w-12 h-12 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-1">All Orders Verified!</h2>
              <p className="text-emerald-100">Today's {totalCount} orders have been cross-checked and are ready for delivery.</p>
            </div>
          )}
        </div>
      </div>

      {/* Error Report Modal */}
      {errorModalOrder && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FiAlertCircle className="w-5 h-5 text-red-500" />
                Report Error for #{errorModalOrder.uniqueId}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Error Type
                  </label>
                  <select
                    value={errorField}
                    onChange={(e) => setErrorField(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Select error type...</option>
                    <option value="pickupTime">Pickup Time Incorrect</option>
                    <option value="entryInstructions">Entry Instructions Wrong</option>
                    <option value="deliveryTime">Delivery Time Issue</option>
                    <option value="driver">Driver Assignment Error</option>
                    <option value="storeOrder">Store Order ID Issue</option>
                    <option value="storeSpend">Store Spend Incorrect</option>
                    <option value="other">Other Issue</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={errorNote}
                    onChange={(e) => setErrorNote(e.target.value)}
                    placeholder="Describe the error you found..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => {
                  setErrorModalOrder(null);
                  setErrorNote('');
                  setErrorField('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReportError}
                disabled={!errorField || !errorNote.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

